/* Shared reference resolution only. Wonderful owns all component/business rules. */
(() => {
  'use strict';
  const fail = message => { throw new Error(`Wonderful reference: ${message}`); };
  const parse = (value, origin, allowedOrigins = []) => {
    const raw = String(value || '').trim();
    if (!raw || raw.length > 2048 || /[\\\u0000-\u0020]/.test(raw) || raw.startsWith('//')) fail('invalid URL');
    let url;
    try { url = new URL(raw, origin); } catch (_) { fail('invalid URL'); }
    // Editor links identify a saved page/template, never a theme to fetch from.
    if (url.origin === 'https://admin.shopify.com') {
      if (url.username || url.password || url.hash) fail('invalid editor URL');
      const editor = url.pathname.match(/^\/store\/([a-z0-9][a-z0-9-]*)\/themes\/([0-9]+)\/editor\/?$/);
      const storeHandles = [origin, ...allowedOrigins].flatMap(value => {
        try {
          const host = new URL(value).hostname;
          return /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(host) ? [host.slice(0, -14)] : [];
        } catch (_) { return []; }
      });
      if (!editor || !storeHandles.includes(editor[1])) fail('editor must belong to this store');
      const editorKeys = new Set(['previewPath', 'section', 'block', 'context']);
      for (const key of url.searchParams.keys()) {
        if (!editorKeys.has(key) || url.searchParams.getAll(key).length !== 1) fail('unsupported editor query');
      }
      const path = url.searchParams.get('previewPath');
      if (!path || !path.startsWith('/pages/')) fail('editor must reference a Wonderful page');
      return parse(path, origin, allowedOrigins);
    }
    if (url.protocol !== 'https:' || url.username || url.password || url.hash ||
        ![origin, ...allowedOrigins].includes(url.origin)) fail('only this store is allowed');
    if (!/^\/pages\/[a-z0-9][a-z0-9-]*\/?$/.test(url.pathname)) fail('expected a Wonderful page URL');
    const allowed = new Set(['view', 'step', 'preview_theme_id', 'pb', '_fd', 'ods_inline_wonderful']);
    for (const key of url.searchParams.keys()) {
      if (!allowed.has(key) || url.searchParams.getAll(key).length !== 1) fail('unsupported query');
    }
    const view = url.searchParams.get('view');
    if (view !== null && !/^[a-zA-Z0-9][a-zA-Z0-9_-]*$/.test(view)) fail('invalid template suffix');
    const result = new URL(url.pathname.replace(/\/$/, ''), origin);
    if (view !== null) result.searchParams.set('view', view);
    return result;
  };
  const parseDocument = html => new DOMParser().parseFromString(html, 'text/html');
  const manifest = step => {
    const node = step.querySelector('template[data-ods-reference-settings]');
    if (!node) fail('missing saved-settings manifest');
    try { return JSON.parse(node.content.textContent); } catch (_) { fail('invalid saved-settings manifest'); }
  };
  async function load(host) {
    const origin = window.location.origin;
    const allowed = JSON.parse(host.dataset.referenceOrigins || '[]');
    const reference = parse(host.dataset.bundleReference, origin, allowed);
    if (!/^\d+$/.test(host.dataset.themeId || '') || !/^\d+$/.test(host.dataset.parentProductId || '')) fail('missing destination identity');
    const request = async url => {
      const options = {credentials:'same-origin', cache:'no-store', redirect:'manual', signal:host.loadAbortController.signal};
      let response = await fetch(url.href, options);
      // Shopify consumes preview_theme_id with a redirect. Retry only the same
      // known URL without that parameter; never follow an arbitrary Location.
      if (response.type === 'opaqueredirect' && url.searchParams.has('preview_theme_id')) {
        const retry = new URL(url);
        retry.searchParams.delete('preview_theme_id');
        response = await fetch(retry.href, options);
      }
      if (!response.ok) fail(`request failed (${response.status})`);
      return response.text();
    };
    const sourceURL = new URL(reference);
    sourceURL.searchParams.set('preview_theme_id', host.dataset.themeId);
    sourceURL.searchParams.set('ods_inline_wonderful', '1');
    const source = parseDocument(await request(sourceURL));
    const identities = source.querySelectorAll('[data-ods-reference-source]');
    if (identities.length !== 1) fail('not a supported Wonderful source');
    const identity = identities[0].dataset;
    if (identity.odsThemeId !== host.dataset.themeId || identity.odsPageUrl !== reference.pathname ||
        identity.odsTemplateName !== 'page' || !identity.odsPageId ||
        (reference.searchParams.has('view') && identity.odsTemplateSuffix !== reference.searchParams.get('view'))) fail('source/template mismatch');
    const sourceSteps = [...source.querySelectorAll('custom-bundle main bundler-step')];
    if (!sourceSteps.length || sourceSteps.length > 25) fail('invalid step count');
    const records = sourceSteps.map(step => {
      const wrapper = step.closest('[id^="shopify-section-template--"]');
      const id = wrapper?.id.replace('shopify-section-', '');
      if (!id || step.dataset.odsReferenceSection !== id || !/^template--\d+__[a-zA-Z0-9_-]+$/.test(id)) fail('missing section identity');
      return {id, settings:manifest(step)};
    });
    if (new Set(records.map(r=>r.id)).size !== records.length) fail('duplicate section');
    const configuredSections = [...source.querySelectorAll('custom-bundle main [id^="shopify-section-template--"]')];
    if (configuredSections.some(section => !section.querySelector('bundler-step'))) fail('unsupported configured section');
    const productURL = new URL(host.dataset.productUrl, origin);
    if (productURL.origin !== origin || !/^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?products\/[a-z0-9-]+$/.test(productURL.pathname)) fail('invalid destination');
    const rendered = [];
    for (let offset=0; offset<records.length; offset+=5) {
      const batch = records.slice(offset, offset+5);
      const url = new URL(productURL);
      url.searchParams.set('preview_theme_id', host.dataset.themeId);
      url.searchParams.set('sections', batch.map(r=>r.id).join(','));
      let response;
      try { response = JSON.parse(await request(url)); } catch (error) { fail(`section response invalid: ${error.message}`); }
      for (const record of batch) {
        if (typeof response[record.id] !== 'string') fail('missing saved section');
        const doc = parseDocument(response[record.id]);
        const steps = doc.querySelectorAll('bundler-step');
        if (steps.length !== 1) fail('invalid rendered section');
        const step = steps[0];
        if (step.dataset.odsReferenceSection !== record.id || step.dataset.odsReferenceParent !== host.dataset.parentProductId ||
            step.dataset.odsReferenceTheme !== host.dataset.themeId || JSON.stringify(manifest(step)) !== JSON.stringify(record.settings)) fail('section settings or destination mismatch');
        step.querySelector('template[data-ods-reference-settings]').remove();
        rendered.push(step);
      }
    }
    const shell = host.querySelector('template[data-ods-reference-shell]');
    if (!shell) fail('missing product-owned wrapper');
    const output = parseDocument(shell.innerHTML);
    const bundle = output.querySelector('custom-bundle');
    const main = output.querySelector('main');
    if (!bundle || !main || output.querySelector('bundler-state')?.dataset.odsParentId !== host.dataset.parentProductId) fail('invalid product-owned wrapper');
    host.dataset.stateKey = `ods-reference-${host.dataset.parentProductId}:${reference.pathname}${reference.search}`;
    bundle.dataset.id = host.dataset.stateKey;
    main.append(...rendered);
    host.dataset.referenceResolved = 'true';
    return {html:output.documentElement.outerHTML, url:productURL.href};
  }
  window.ODSWonderfulReference = Object.freeze({parse, load});
})();
